import React, { useMemo, useCallback } from 'react';
import { Builder } from '@builder.io/react';
import { useSelector } from 'react-redux';
import { selectProducts } from '../../../../../../store/products';
import ProductsTabVertical from '../../../../../general/productsTabVertical';
import AppealWidgetNewWithCarousel from '../../../../../../pages/MATWAppeal/components/appealWidgetNewWithCarousel';

/* =============================================================================
   Utilities
============================================================================= */

function useFlatProductsTree(productsTree) {
  return useMemo(() => {
    const flat = [];
    if (!productsTree) return flat;

    const asArray = Array.isArray(productsTree)
      ? productsTree
      : Object.values(productsTree);

    asArray.forEach((cat) => {
      (cat?.sub_categories || []).forEach((sub) => {
        (sub?.products || []).forEach((p) => flat.push(p));
      });
    });

    return flat;
  }, [productsTree]);
}

function useProductIndex(flatProducts) {
  return useMemo(() => {
    const index = new Map();
    flatProducts.forEach((p) => {
      if (p?.creator) index.set(String(p.creator).trim(), p);
      if (p?._id) index.set(String(p._id).trim(), p);
      if (p?.id) index.set(String(p.id).trim(), p);
    });
    return index;
  }, [flatProducts]);
}

function useResolver(productIndex) {
  return useCallback(
    (idsCsv) => {
      if (!idsCsv) return [];
      return idsCsv
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((key) => productIndex.get(key))
        .filter(Boolean);
    },
    [productIndex]
  );
}

/* -------- image helpers (file picker or URL) -------- */

function extractImageUrl(val) {
  if (!val) return '';
  if (typeof val === 'string') return val;

  if (Array.isArray(val)) {
    const first = val[0];
    if (!first) return '';
    return (
      first.url ||
      first.src ||
      first.image ||
      first.fileUrl ||
      (typeof first === 'string' ? first : '')
    ) || '';
  }

  if (typeof val === 'object') {
    return (
      val.url ||
      val.src ||
      val.image ||
      val.fileUrl ||
      (val.value && (val.value.url || val.value.src || val.value.fileUrl)) ||
      ''
    );
  }

  return '';
}

/**
 * Normalize a product/card to the shape your carousel expects.
 * `defaultStatic` controls whether we default to static behavior:
 *   - true  → for CUSTOM tabs (safe default, shows "DONATE NOW" if no text)
 *   - false → for ID tabs (preserve original behavior)
 */
function normalizeCardShape(cardLike, { defaultStatic = false } = {}) {
  const c = cardLike || {};
  const imageUrl = extractImageUrl(
    c.image ?? c.image_file ?? c.imageUrl ?? c.image_link
  );

  const isStatic =
    typeof c.isStaticProduct === 'boolean' ? c.isStaticProduct : !!defaultStatic;

  // For custom cards, give a safe default CTA if not provided.
  const staticLinkText =
    c.staticLinkText || (isStatic && defaultStatic ? 'DONATE NOW' : '');

  return {
    ...c,
    name: c.name || c.alternativeName || '',
    alternativeName: c.alternativeName || '',
    description: c.description || '',
    short_description: c.short_description || '',
    image_link: imageUrl,
    alt: c.alt || c.image_alt || '',
    status: typeof c.status === 'number' ? c.status : 1,
    isStaticProduct: isStatic,
    staticLink: c.staticLink || '',
    staticLinkText,
    linkBtnClasses: c.linkBtnClasses || '',
  };
}

/** Is this card renderable enough? */
function isMeaningfulCard(p) {
  if (!p) return false;
  const hasCatalogId = !!(p.creator || p._id || p.id);
  const hasAnyContent = !!(p.name || p.image_link || p.short_description || p.description);
  const hasStatic = p.isStaticProduct && (p.staticLink || p.image_link || p.name || p.short_description);
  return hasCatalogId || hasAnyContent || hasStatic;
}

/** Sanitize a list before rendering, with mode-aware defaults */
function sanitizeProducts(list, { defaultStatic }) {
  return (Array.isArray(list) ? list : [])
    .filter(Boolean)
    .map((item) => normalizeCardShape(item, { defaultStatic }))
    .filter(isMeaningfulCard);
}

/**
 * Apply per-ID overrides (shallow merge) to a resolved product list.
 * We do NOT force `isStaticProduct` here; let normalize handle defaults by mode.
 */
function applyOverrides(list, overrides = []) {
  if (!overrides?.length) return list;

  const map = new Map();
  overrides.forEach((o) => {
    if (!o?.matchId) return;
    map.set(String(o.matchId).trim(), o);
  });

  return list.map((p) => {
    if (!p) return p;
    const keysToTry = [p?.creator, p?._id, p?.id].map((k) =>
      k == null ? '' : String(k).trim()
    );
    const foundKey = keysToTry.find((k) => k && map.has(k));
    if (!foundKey) return p;

    const { matchId, ...fields } = map.get(foundKey);
    return { ...p, ...fields }; // normalized later per mode
  });
}

/* =============================================================================
   Safety wrapper for the carousel
============================================================================= */

class CarouselErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err, info) {
    console.error('Appeal carousel render error:', err, info);
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

const SafeAppealCarousel = ({ items, ...rest }) => {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  if (list.length === 0) return null;
  return (
    <CarouselErrorBoundary>
      <AppealWidgetNewWithCarousel {...rest} allProducts={list} />
    </CarouselErrorBoundary>
  );
};

/* =============================================================================
   Main dynamic component
============================================================================= */

const ProductsTabsVerticalDynamic = (props) => {
  const productsTree = useSelector((state) => selectProducts(state));
  const flatProducts = useFlatProductsTree(productsTree);
  const productIndex = useProductIndex(flatProducts);
  const resolveProducts = useResolver(productIndex);

  const rawSections = Array.isArray(props.sections) ? props.sections : [];
  const visibleSections = rawSections.filter((s) => s?.isTabsVisible !== false);

  const computedSections = useMemo(() => {
    return visibleSections
      .map((section) => {
        const {
          title = '',
          description = '',
          isCarousel = true,
          hideIfEmpty = true,
          mode = 'ids', // 'ids' | 'custom'
          productIds,
          productsCsv, // legacy fallback
          overrides = [],
          customCards = [],
        } = section || {};

        let rawList = [];
        let defaultStatic = false;

        if (mode === 'custom') {
          // Custom tabs default to static behavior (safe CTA default)
          defaultStatic = true;
          rawList = (customCards || []);
        } else {
          // ID tabs: preserve catalog behavior (NO static default)
          defaultStatic = false;
          const idsCsv = productIds ?? productsCsv ?? '';
          const resolved = resolveProducts(idsCsv);
          rawList = applyOverrides(resolved, overrides);
        }

        const list = sanitizeProducts(rawList, { defaultStatic });

        if (hideIfEmpty && list.length === 0) {
          return null;
        }

        return {
          title,
          description,
          isCarousel,
          products: (
            <SafeAppealCarousel
              className="bg-transparent !py-0"
              items={list}
              isHomePage
            />
          ),
        };
      })
      .filter(Boolean);
  }, [visibleSections, resolveProducts]);

  if (!computedSections.length) return null;

  return <ProductsTabVertical sections={computedSections} />;
};

/* =============================================================================
   Builder registration (with image file pickers)
============================================================================= */

Builder.registerComponent(ProductsTabsVerticalDynamic, {
  name: 'ProductsTabsVerticalDynamic',
  inputs: [
    {
      name: 'sections',
      friendlyName: 'Tabs',
      type: 'list',
      subFields: [
        { name: 'title', type: 'text', defaultValue: 'Most Needed', friendlyName: 'Tab Title' },
        { name: 'description', type: 'longText', friendlyName: 'Description' },
        { name: 'isCarousel', type: 'boolean', defaultValue: true, friendlyName: 'Show as Carousel' },

        // Visibility / safety
        { name: 'isTabsVisible', type: 'boolean', defaultValue: true, friendlyName: 'Show Tab' },
        {
          name: 'hideIfEmpty',
          type: 'boolean',
          defaultValue: true,
          friendlyName: 'Hide Tab If Empty',
          helperText: 'If this tab resolves to no usable items, do not render it.',
        },

        // Data source mode
        {
          name: 'mode',
          type: 'text',
          enum: ['ids', 'custom'],
          defaultValue: 'ids',
          friendlyName: 'Tab Mode (IDs / Custom)',
          helperText: 'Use "ids" to list catalog products by ID, or "custom" for fully custom cards.',
        },

        // IDs mode: exact, comma-separated (creator/_id/id)
        {
          name: 'productIds',
          type: 'text',
          friendlyName: 'Products (comma-separated IDs)',
          helperText: 'Exact IDs (creator/_id/id). Order here = order on page. Example: EMR-PAL-25-020, RM24-ZM-013',
        },

        // Optional: legacy field name
        {
          name: 'productsCsv',
          type: 'text',
          friendlyName: 'Products CSV (legacy - optional)',
        },

        // Per-ID overrides (only used when mode === 'ids')
        {
          name: 'overrides',
          type: 'list',
          friendlyName: 'Per-ID Overrides',
          subFields: [
            { name: 'matchId', type: 'text', friendlyName: 'Match ID (creator/_id/id)' },
            { name: 'name', type: 'text' },
            { name: 'alternativeName', type: 'text' },
            { name: 'description', type: 'longText' },
            { name: 'short_description', type: 'longText' },

            // Image file picker (preferred)
            {
              name: 'image',
              type: 'file',
              friendlyName: 'Image',
              allowedFileTypes: ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'],
            },
            { name: 'image_alt', type: 'text', friendlyName: 'Image Alt' },

            // Optional legacy URL
            { name: 'image_link', type: 'text', friendlyName: 'Image URL (legacy - optional)' },

            { name: 'alt', type: 'text', friendlyName: 'Alt (optional)' },
            { name: 'isStaticProduct', type: 'boolean', defaultValue: false },
            { name: 'staticLink', type: 'text' },
            { name: 'staticLinkText', type: 'text' },
            { name: 'linkBtnClasses', type: 'text' },
          ],
        },

        // Custom mode: fully custom cards
        {
          name: 'customCards',
          type: 'list',
          friendlyName: 'Custom Cards',
          subFields: [
            { name: 'name', type: 'text', friendlyName: 'Title' },
            { name: 'alternativeName', type: 'text' },
            { name: 'description', type: 'longText' },
            { name: 'short_description', type: 'longText' },

            // Image file picker (preferred)
            {
              name: 'image',
              type: 'file',
              friendlyName: 'Image',
              allowedFileTypes: ['png', 'jpg', 'jpeg', 'webp', 'gif', 'svg'],
            },
            { name: 'image_alt', type: 'text', friendlyName: 'Image Alt' },

            // Optional legacy URL
            { name: 'image_link', type: 'text', friendlyName: 'Image URL (legacy - optional)' },

            { name: 'alt', type: 'text' },
            { name: 'isStaticProduct', type: 'boolean', defaultValue: true },
            { name: 'staticLink', type: 'text' },
            { name: 'staticLinkText', type: 'text' },
            { name: 'linkBtnClasses', type: 'text' },
          ],
        },
      ],
    },
  ],
});

export default ProductsTabsVerticalDynamic;
